// @flow strict

import * as React from 'react';
import type {
  RouteContext,
  EnvironmentContext,
  DeviceContext,
  PreferenceContext,
  VisitorContext,
  DebugContext,
  AuthenticateContext,
  TravelInfoContext,
} from './types';

// **********************************************************
// PLEASE SEE DOCUMENTATION FOR THESE CONTEXTS IN ./types.jsx
// **********************************************************

const Environment = React.createContext<EnvironmentContext>({
  locale: { code: 'en-US', isRtl: false, language: 'en' },
  cdnPrefix: '',
  domainName: '',
  version: 'unknown',
});

const Device = React.createContext<DeviceContext>({
  id: '',
  isWebview: false,
  viewportCategory: 'MOBILE',
  userAgentCategory: 'MOBILE',
  os: { family: 'UNKNOWN', majorVersion: 0 },
  browser: { family: 'UNKNOWN', majorVersion: 0 },
  securityToken: '',
});

const Route = React.createContext<RouteContext>([
  {
    page: 'Unknown',
    params: {},
    path: '/Unknown',
    fragment: '',
  },
  {
    uid: 'LIT@-Unknown------------',
    navigate: () => {},
    navigateToFragment: () => {},
  },
]);

const Visitor = React.createContext<VisitorContext>({
  userId: null,
  sessionId: null,
});
const Authenticate = React.createContext<AuthenticateContext>(() => {});

const Preference = React.createContext<PreferenceContext>({
  currency: 'USD',
  update: {
    setCurrency: () => undefined,
  },
});

const Debug = React.createContext<DebugContext>({
  serverName: '',
  drsInfo: '',
  ssrLogs: [],
  renderError: null,
  stagingError: null,
  debugTool: DEBUG,
  serviceCalls: [],
});

const TravelInfo = React.createContext<TravelInfoContext>({
  attractions: {
    singleDate: null,
    toDate: null,
    fromDate: null,
    adults: null,
    children: null,
  },
  restaurants: {
    date: null,
  },
  hotels: {
    checkInDate: new Date(),
    checkOutDate: new Date(),
    defaultDates: true,
    rooms: 0,
    adults: 0,
    children: 0,
    childAgesPerRoom: [],
  },
  flights: {
    oneWayDate: null,
    departureDate: null,
    returnDate: null,
  },
  update: {
    attractions: {
      setSingleDate: () => undefined,
      setDates: () => undefined,
      setPassengers: () => undefined,
    },
    restaurants: {
      setDate: () => undefined,
    },
    hotels: {
      setStayDates: () => undefined,
      setGuestInfo: () => undefined,
    },
    flights: {
      setOneWayDate: () => undefined,
      setRoundTripDates: () => undefined,
    },
  },
});

if (DEBUG) {
  Environment.displayName = 'Environment';
  Device.displayName = 'Device';
  TravelInfo.displayName = 'TravelInfo';
  Preference.displayName = 'Preference';
  Visitor.displayName = 'Visitor';
  Route.displayName = 'Route';
  Authenticate.displayName = 'Authenticate';
  Debug.displayName = 'Debug';
}

export { Route, Visitor, Preference, Environment, Device, Debug, Authenticate, TravelInfo };

export type * from './types';
